import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';
import { requirePermission } from '@/lib/route-guards';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { QueryError } from '@/components/QueryError';
import { FuelStatsPanel, type FuelStats } from '@/components/fuel/FuelStatsPanel';

import type { FuelTransaction, Paginated } from '@logistech/ui-shared';

export const Route = createFileRoute('/_authed/fuel')({
  beforeLoad: () => requirePermission('fuel.read'),
  component: FuelPage,
});

export type FuelPeriod = '30d' | '90d' | '12m';
const PERIODS: { key: FuelPeriod; label: string }[] = [
  { key: '30d', label: '30 jours' },
  { key: '90d', label: '90 jours' },
  { key: '12m', label: '12 mois' },
];

const DAY = 24 * 60 * 60 * 1000;
/** Début de période : glissant pour 30/90 j, 1er du mois il y a 11 mois pour 12 mois (12 mois pleins dans monthly). */
export const periodFrom = (period: FuelPeriod, now = new Date()): Date =>
  period === '12m'
    ? new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 11, 1))
    : new Date(now.getTime() - (period === '30d' ? 30 : 90) * DAY);

export function FuelPage() {
  // Début figé au choix de la période : clé de requête stable
  const [period, setPeriod] = useState<{ key: FuelPeriod; from: string }>(() => ({ key: '30d', from: periodFrom('30d').toISOString() }));
  const statsQuery = useQuery({
    queryKey: ['fuel-stats', period.from],
    queryFn: async () => (await api.get<FuelStats>(`/fuel/stats?${new URLSearchParams({ from: period.from }).toString()}`)).data,
    placeholderData: keepPreviousData,
  });
  const stats = statsQuery.data;

  const query = useQuery({
    queryKey: ['fuel-transactions'],
    queryFn: async () => (await api.get<Paginated<FuelTransaction>>('/fuel/transactions?limit=100')).data,
  });
  const { data, isLoading } = query;

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">Carburant</h1>
        <div className="flex gap-1" role="group" aria-label="Période">
          {PERIODS.map((p) => (
            <Button
              key={p.key}
              size="sm"
              variant={period.key === p.key ? 'default' : 'outline'}
              aria-pressed={period.key === p.key}
              onClick={() => setPeriod({ key: p.key, from: periodFrom(p.key).toISOString() })}
            >
              {p.label}
            </Button>
          ))}
        </div>
      </div>
      <QueryError query={statsQuery} />
      <QueryError query={query} />

      {statsQuery.isLoading && (
        <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
        </div>
      )}
      {stats && stats.totals.transactions === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">Aucune donnée carburant sur la période</CardContent>
        </Card>
      )}
      {stats && stats.totals.transactions > 0 && <FuelStatsPanel stats={stats} />}

      <div className="bg-card rounded-lg border">
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="font-semibold">Transactions récentes</h2>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Véhicule</TableHead>
              <TableHead>Conducteur</TableHead>
              <TableHead>Litres</TableHead>
              <TableHead>Prix/L</TableHead>
              <TableHead>Total</TableHead>
              <TableHead>Station</TableHead>
              <TableHead>Odo</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={i}>{Array.from({ length: 8 }).map((__, j) => <TableCell key={j}><Skeleton className="h-4" /></TableCell>)}</TableRow>
            ))}
            {data?.data?.map((t) => (
              <TableRow key={t.id}>
                <TableCell>
                  {new Date(t.transactionDate).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
                </TableCell>
                <TableCell>{t.vehicle?.plate ?? '—'}</TableCell>
                <TableCell>
                  {t.driver ? `${t.driver.firstName} ${t.driver.lastName}` : '—'}
                  {t.source === 'driver_app' && <span className="ml-1 text-xs text-muted-foreground">(app)</span>}
                </TableCell>
                <TableCell>{t.liters.toFixed(2)} L</TableCell>
                <TableCell>{`${t.pricePerLiter.toLocaleString('fr-FR')} ${t.currency}`}</TableCell>
                <TableCell>{`${t.totalAmount.toLocaleString('fr-FR')} ${t.currency}`}</TableCell>
                {/* Station saisie par le chauffeur : dans les notes (« Station : X ») */}
                <TableCell>{t.station?.name ?? t.notes?.replace(/^Station : /, '') ?? '—'}</TableCell>
                <TableCell>{t.odometerKm ? `${t.odometerKm.toLocaleString('fr-FR')} km` : '—'}</TableCell>
              </TableRow>
            ))}
            {data && (data.data?.length ?? 0) === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Aucune transaction</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
